import apiClient from "../helpers/apiClient";
import { format, isValid, parseISO } from "date-fns";

const KNOWN_LIST_KEYS = [
  "departments",
  "department_info",
  "departmentInfo",
  "data",
  "rows",
  "result",
  "results",
  "items",
];

const DETAIL_KEYS = [
  "department",
  "department_info",
  "departmentInfo",
  "data",
  "result",
  "details",
];

export const departmentService = {
  getDepartments,
  getDepartmentDetails,
  mapDepartment,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  setDepartmentStatus,
};

function firstValue(source, keys, fallback = null) {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) {
      return source[key];
    }
  }
  return fallback;
}

function getNestedValue(source, keyPath) {
  return keyPath.split(".").reduce((value, key) => value?.[key], source);
}

function findListPayload(payload) {
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload?.department_info)) return payload.department_info;

  for (const key of KNOWN_LIST_KEYS) {
    const value = getNestedValue(payload, key);
    if (Array.isArray(value)) return value;
  }

  if (payload && typeof payload === "object") {
    const firstArray = Object.values(payload).find(Array.isArray);
    if (firstArray) return firstArray;
  }

  return [];
}

function findDetailPayload(payload) {
  if (Array.isArray(payload)) return findDetailPayload(payload[0]);
  if (!payload || typeof payload !== "object") return null;

  for (const key of DETAIL_KEYS) {
    const value = getNestedValue(payload, key);
    if (value && value !== payload) {
      const detail = findDetailPayload(value);
      if (detail) return detail;
    }
  }

  if (
    payload.department_id !== undefined ||
    payload.deapartment_name !== undefined ||
    payload.department_name !== undefined ||
    payload.id !== undefined
  ) {
    return payload;
  }

  for (const value of Object.values(payload)) {
    if (value && typeof value === "object") {
      const detail = findDetailPayload(value);
      if (detail) return detail;
    }
  }

  return payload;
}

function formatDepartmentDate(value) {
  if (value === null || value === undefined || value === "") return "-";

  const date = value instanceof Date
    ? value
    : typeof value === "string"
      ? parseISO(value)
      : new Date(value);

  return isValid(date) ? format(date, "dd MMM yyyy") : "-";
}

function mapDepartment(raw = {}) {
  const id = firstValue(raw, ["department_id", "id", "Department_Id"]);
  const name = firstValue(
    raw,
    ["deapartment_name", "department_name", "name", "Department_Name"],
    "-"
  );
  const statusId = firstValue(raw, [
    "department_status_id",
    "status_id",
    "Department_Status_Id",
  ]);
  const statusLabel = firstValue(
    raw,
    ["department_status_name", "status_name", "status"],
    "-"
  );
  const createdAtRaw = firstValue(raw, ["encoded_date", "created_at"]);
  const updatedAtRaw = firstValue(raw, ["last_changed_date", "updated_at"]);

  return {
    id,
    name,
    statusId,
    statusLabel,
    isActive: statusId === 1,
    createdBy: firstValue(raw, ["encoded_by_user_name"], "-"),
    createdAt: formatDepartmentDate(createdAtRaw),
    createdAtRaw,
    lastChangedBy: firstValue(raw, ["last_changed_by_user_name"], "-"),
    updatedBy: firstValue(raw, ["last_changed_by_user_name"], "-"),
    updatedAt: formatDepartmentDate(updatedAtRaw),
    updatedAtRaw,
    raw,
  };
}

async function getDepartments(options = {}) {
  const response = await apiClient.get("/hr/departments", { signal: options.signal });
  const list = findListPayload(response?.data);

  return {
    raw: list,
    departments: list.map(mapDepartment).filter((department) => department.id !== undefined && department.id !== null && department.id !== ""),
    response: response?.data,
  };
}

async function getDepartmentDetails(id, options = {}) {
  const response = await apiClient.get(`/hr/departments/${id}`, { signal: options.signal });
  const detail = findDetailPayload(response?.data);

  return {
    raw: detail,
    department: detail ? mapDepartment(detail) : null,
    response: response?.data,
  };
}

function extractWriteResult(response) {
  const data = response?.data;
  const messageObject = Array.isArray(data?.result_status)
    ? data.result_status[0]
    : Array.isArray(data)
      ? data[0]
      : data;
  const messageText = messageObject?.message_text || messageObject?.Message_Text;
  const refNo = messageObject?.message_ref_no ?? messageObject?.Message_Ref_No ?? null;

  return {
    message:
      messageText !== undefined && messageText !== null && messageText !== ""
        ? messageText
        : "Saved successfully.",
    refNo,
  };
}

async function handleWriteRequest(request) {
  try {
    const response = await request();
    return extractWriteResult(response);
  } catch (error) {
    throw new Error(
      error.response?.data?.msg ||
        error.response?.data?.message ||
        "Something went wrong"
    );
  }
}

function createDepartment(name) {
  return handleWriteRequest(() =>
    apiClient.post("/hr/departments", { department_name: name })
  );
}

function updateDepartment(id, name, statusId) {
  return handleWriteRequest(() =>
    apiClient.put(`/hr/departments/${id}`, {
      department_name: name,
      department_status_id: statusId,
    })
  );
}

function deleteDepartment(id) {
  return handleWriteRequest(() => apiClient.delete(`/hr/departments/${id}`));
}

function setDepartmentStatus(id, statusId) {
  return handleWriteRequest(() =>
    apiClient.patch(`/hr/departments/${id}/status`, {
      department_status_id: statusId,
    })
  );
}
